import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useAuth } from './Auth/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MAX_POLL_ATTEMPTS = 60; // Poll for max 60 seconds (30 requests × 2 seconds)
const POLL_INTERVAL = 2000; // Poll every 2 seconds

function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userId, setIsSubscribed } = useAuth();

  const [status, setStatus] = useState('confirming'); // 'confirming', 'success', 'error'
  const [message, setMessage] = useState('Confirming your subscription...');
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!userId) {
      setStatus('error');
      setMessage('User not authenticated. Please log in again.');
      return;
    }

    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid checkout session. Please try again.');
      return;
    }

    const pollSubscriptionStatus = async () => {
      try {
        // Get JWT token
        const authSession = await fetchAuthSession();
        const idToken = authSession.tokens?.idToken?.toString();

        if (!idToken) {
          throw new Error('Failed to get authentication token');
        }

        // Call get-subscription-status Lambda
        const { data } = await axios.get(`${API_BASE_URL}/api/auth/subscription-status`, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 5000
        });

        if (data.isSubscribed) {
          // Subscription confirmed!
          setStatus('success');
          setMessage('✓ Your subscription is active! Redirecting to dashboard...');
          setIsSubscribed(true);

          // Redirect to dashboard after 2 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else if (pollCount >= MAX_POLL_ATTEMPTS) {
          // Polling timeout - subscription may still be processing
          setStatus('error');
          setMessage(
            'Subscription confirmation is taking longer than expected. ' +
            'Please check your email or go to the dashboard to verify your subscription status.'
          );
        } else {
          // Continue polling
          setPollCount(pollCount + 1);
        }
      } catch (err) {
        console.error('SubscriptionSuccess: Poll error:', err.message);

        if (pollCount >= MAX_POLL_ATTEMPTS) {
          setStatus('error');
          setMessage(
            'Could not confirm subscription. ' +
            'Please check your email or contact support if you were charged.'
          );
        }
        // Continue polling on error
        setPollCount(pollCount + 1);
      }
    };

    // Initial poll
    pollSubscriptionStatus();

    // Set up polling interval (only if not yet confirmed)
    if (status === 'confirming' && pollCount < MAX_POLL_ATTEMPTS) {
      const timer = setTimeout(() => {
        pollSubscriptionStatus();
      }, POLL_INTERVAL);

      return () => clearTimeout(timer);
    }
  }, [pollCount, userId, sessionId, navigate, status, setIsSubscribed]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center font-quicksand px-4">
      <div className="w-full max-w-md">
        {status === 'confirming' && (
          <div className="text-center">
            <div className="mb-6">
              <div className="inline-block">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-study-pink"></div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-study-black mb-4">Processing Payment</h1>
            <p className="text-study-black text-lg mb-2">{message}</p>
            <p className="text-gray-600 text-sm">
              Attempt {pollCount + 1} of {MAX_POLL_ATTEMPTS}
            </p>
            <p className="text-gray-500 text-xs mt-4">
              Session ID: {sessionId?.substring(0, 20)}...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mb-6">
              <div className="inline-block text-6xl">✓</div>
            </div>
            <h1 className="text-2xl font-bold text-study-black mb-4">Subscription Activated!</h1>
            <p className="text-study-black text-lg mb-2">{message}</p>
            <p className="text-gray-600 text-sm">You now have unlimited access to all quizzes and study guides.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mb-6">
              <div className="inline-block text-6xl">⚠</div>
            </div>
            <h1 className="text-2xl font-bold text-red-600 mb-4">Confirmation Pending</h1>
            <p className="text-study-black text-lg mb-2">{message}</p>
            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-study-pink text-study-black px-4 py-2 rounded-lg hover:bg-study-yellow transition-all w-full font-semibold"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-gray-300 text-study-black px-4 py-2 rounded-lg hover:bg-gray-400 transition-all w-full"
              >
                Back to Pricing
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SubscriptionSuccess;
